import { clsx } from "clsx";
import type { ButtonHTMLAttributes } from "react";

/**
 * The prototype's button (spec §8.21): 36 px high, 6 px radius, ink for the primary action, a
 * bordered surface for the secondary one, and a quiet ghost for everything in a toolbar. On a narrow
 * screen a long label wraps onto a second line.
 */
export function Button({
  className,
  variant = "primary",
  size = "md",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm" | "md";
}) {
  return (
    <button
      className={clsx(
        "inline-flex max-w-full items-center justify-center gap-[7px] whitespace-nowrap rounded-sm border font-medium tracking-[-0.005em] transition disabled:cursor-not-allowed disabled:opacity-50",
        // a narrow screen lets a long label wrap instead of running past the edge (WCAG 1.4.10)
        "narrow:h-auto narrow:whitespace-normal narrow:py-1 narrow:text-center",
        size === "sm"
          ? "h-[30px] px-2.5 text-[13px] narrow:min-h-[30px]"
          : "h-9 px-3.5 text-[13.5px] narrow:min-h-9",
        // one border colour per variant: two colour utilities on one element would let the CSS order decide
        variant === "primary" &&
          "border-transparent bg-neutral-900 text-white hover:bg-black dark:bg-neutral-100 dark:text-neutral-900 dark:hover:bg-white",
        variant === "secondary" &&
          "border-neutral-300 bg-[var(--surface)] text-neutral-900 hover:border-neutral-700 hover:bg-neutral-100 dark:text-neutral-100 dark:hover:bg-neutral-800",
        variant === "ghost" &&
          "border-transparent text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-800 dark:hover:text-neutral-100",
        variant === "danger" &&
          "border-neutral-300 bg-[var(--surface)] text-err hover:border-err hover:bg-err-tint dark:hover:bg-neutral-800",
        className,
      )}
      {...props}
    />
  );
}
